<template>
    <div id="root">
        <div v-if="isOneTrustPopupOpened && !isOneTrustPopupClosed" class="onetrust-overlay" />
        <router-view id="router-view" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import cleverbridge from '@core/mixins/cleverbridge.js';
import utm from '@core/mixins/utm.js';
import { scrollToAnchorMenuTarget } from '@core/slices/pages/anchor-menu/component/navigation';
import { ONETRUST_NATIVE_CONSENT_CHANGED } from '@model/const/onetrust';
import type { SSRContext } from '@model/ssr/context';
import 'the-new-css-reset/css/reset.css';
import 'highlight.js/styles/atom-one-light.css';
import '@core/styles/vars.pcss';
import '@core/styles/defaults.pcss';
import '@core/styles/utilities/utilities.css';
import '@core/styles/vars/grid-system.scss';
import '@core/styles/spacings.scss';
import '@core/styles/cols.scss';
import '@core/styles/custom-fonts.scss';
// NB: font stylesheet is preload + async import in root.html for better UX

declare global {
    interface Window {
        google: any;
        markerClusterer: any;
        dataLayer: unknown[];
        ga: any;
        VWO_OUTCOME: 'pending' | 'regular' | 'redirect',
    }
}

export default defineComponent({
    name: 'AppRoot',

    mixins: [utm, cleverbridge],
    data() {
        return {
            isOneTrustPopupClosed: true,
            isOneTrustPopupOpened: true,
            oneTrustObserver: null as MutationObserver | null,
        };
    },

    async mounted() {
        await this.deleteSensitiveQueryParams();
        this.pushGaEvents();
        this.handleClick();
        this.handleURLHash();
        this.storeLocalUTM();
        this.storeSessionUTM();
        this.observeOneTrustPopup();
        window.addEventListener(ONETRUST_NATIVE_CONSENT_CHANGED, this.refreshOneTrustPopupState);
    },

    beforeMount() {
        window.addEventListener('load', this.addLabelUTM);
    },

    beforeUnmount() {
        window.removeEventListener('load', this.addLabelUTM);
        if (this.oneTrustObserver) {
            this.oneTrustObserver.disconnect();
        }
        window.removeEventListener(ONETRUST_NATIVE_CONSENT_CHANGED, this.refreshOneTrustPopupState);
    },

    methods: {
        observeOneTrustPopup() {
            const targetNode = document.body;
            const config = { childList: true, subtree: true };

            this.oneTrustObserver = new MutationObserver(() => {
                this.refreshOneTrustPopupState();
            });

            this.oneTrustObserver.observe(targetNode, config);

            this.refreshOneTrustPopupState();
        },

        refreshOneTrustPopupState() {
            if (typeof window?.OneTrust?.IsAlertBoxClosed === 'function') {
                this.isOneTrustPopupOpened = window.Optanon.GetDomainData?.()?.ShowAlertNotice;
                this.isOneTrustPopupClosed = window.OneTrust.IsAlertBoxClosed();
            }
        },

        async deleteSensitiveQueryParams() {
            // we clear potential first_name and last_name params to prevent them from being sent to facebook
            if (Object.keys(this.$route?.query).length) {
                this.$store.commit('trial/SET_QUERY_STRING_DATA', this.$route.query);

                const query = { ...this.$route.query };
                const expectedKeys = [
                    'osauid',
                    'aan',
                    'company',
                    'email',
                    'first_name',
                    'last_name',
                    'phone',
                    'country',
                    'currency',
                    'price',
                    'opportunity_id',
                    'tenantId',
                ];

                expectedKeys.forEach((key) => (delete query[key]));

                if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
                    await this.$router.replace({
                        path: this.$route.path,
                        query,
                        hash: this.$route.hash,
                    });
                }
            }
        },

        pushGaEvents() {
            window.dataLayer = window.dataLayer || [];

            this.initAnonymousGATrackerDelayed(new Date());

            const customPageEvents = this.$store.state.pages?.page?.settings?.pushEventToDataLayer || [];
            customPageEvents.forEach((item) => window.dataLayer.push(item));
        },

        addLabelUTM() {
            if (!this.getSessionUTM.length) {
                return;
            }
            Array.from(document.getElementsByTagName('a')).forEach((element: HTMLAnchorElement): void => {
                const url = this.setQueryString(element.href);
                if (url) element.setAttribute('href', url);
            });
        },

        handleClick(): void {
            document.body.addEventListener('click', (e: any) => {
                // WEB-42890: Support dynamically injected surl links from the api content
                if (e.target.dataset?.surl) {
                    e.preventDefault();
                    const surl = JSON.parse(e.target.dataset.surl);
                    const options = this.storeOptionsSURL(surl);
                    this.redirectToCleverbridge(options);
                    return;
                }
                if (e.target.dataset?.event) {
                    window.dataLayer.push({
                        eventLabel: e.target.href,
                        ...e.target.dataset,
                    });
                }
            });
        },

        handleURLHash(): void {
            const hash = this.$route.hash.replace('/', ''); // TODO: why replace?
            if (!hash) {
                return;
            }

            if (window.location.hash !== hash) {
                window.location.hash = hash;
            }

            // The app can mount into an already complete document — a late hydration, or the
            // awaits above — and `readystatechange` would then never fire again. Both paths
            // lead through the same guard inside `scrollToSlice`, so only one of them scrolls.
            this.scrollToSlice(hash);
            document.addEventListener('readystatechange', () => this.scrollToSlice(hash));
        },

        scrollToSlice(hash: string): void {
            if (document.readyState !== 'complete') {
                return;
            }

            // hash can contain virtually anything so querySelector can throw an invalid selector error
            let el = null;
            try {
                // Numeric IDs such as #07 are valid IDs, but not CSS selectors.
                el = (hash.startsWith('#') ? document.getElementById(hash.slice(1)) : null) ??
                    document.querySelector(hash);
            } catch (e) {
                // NO OP
            }

            if (!el) {
                return;
            }

            if (scrollToAnchorMenuTarget(el)) return;

            window.scroll({
                top: el.getBoundingClientRect().top + window.scrollY - this.getStickyScrollMargin(),
                behavior: 'smooth',
            });
        },

        // The anchor menu margin already includes its top offset for panels above it.
        // Take the larger margin to avoid counting those panels twice.
        getStickyScrollMargin(): number {
            const anchorMenuMargin = parseFloat(
                getComputedStyle(document.body).getPropertyValue('--anchor-menu-scroll-margin'),
            ) || 0;

            const otherStickyHeight = Array.from(document.querySelectorAll('.directive-v-sticky:not(.s-anchor-menu)'))
                .map((sticky: HTMLElement) => sticky.offsetHeight || 0)
                .reduce((acc: number, next: number) => acc + next, 0);

            return Math.max(anchorMenuMargin, otherStickyHeight);
        },

        storeLocalUTM(): void {
            if (!window.localStorage) {
                return;
            }

            const utmContent = new URL(window.location.href).searchParams.get('utm_content');
            const allowedKeys = new Set(['smart', 'Smart', 'SmartDisplay']);

            if (allowedKeys.has(utmContent)) {
                localStorage.setItem('utm_content', utmContent);
            }
        },

        storeSessionUTM(): void {
            if (!window.sessionStorage) {
                return;
            }

            const searchParams = new URL(window.location.href).searchParams;

            const labels = [
                'utm_campaign',
                'utm_content',
                'utm_medium',
                'utm_source',
                'utm_term',
                'x-audience',
                'x-opp-id',
                'x-tenant_id',
                'x-wave',
            ];

            labels
                .map((key) => ({ key, val: searchParams.get(key) }))
                .filter((pair) => Boolean(pair.val))
                .forEach((x) => sessionStorage.setItem(x.key, x.val));
        },

        initAnonymousGATrackerDelayed(startedAt: any): void {
            // Universal Google Analytics now require a tracker name for its calls: `ga("gtm5.send", ...)`
            // For some 3rd party libs (Onetrust, maybe more)
            // it's necessary to have an unnamed tracker: `ga("send", ...)`
            // As GTM doesn't fire a `ga_loaded` event of any kind, we must check it in a timer callback

            const MAX_DETECT_TIME = 10000; // ms
            const DETECT_INTERVAL = 100; // ms
            const CURRENT:any = new Date();

            if (window.ga && window.ga.loaded && window.ga.getAll()[0]) {
                const defaultTracker = window.ga.getAll()[0];
                const opts = {
                    cookieDomain: 'auto',
                    allowLinker: true,
                };
                window.ga('create', defaultTracker.get('trackingId'), opts);
            } else if (CURRENT - startedAt > MAX_DETECT_TIME) {
                // It took too long, we just give up
                // return;
            } else {
                setTimeout(() => this.initAnonymousGATrackerDelayed(startedAt), DETECT_INTERVAL);
            }
        },
    },

    async serverPrefetch(): Promise<void> {
        if (!import.meta.env.SSR) return;

        const ctx = this.$ssrContext as SSRContext;

        switch (ctx.req.l10n.localizationStrategy) {
            case 'locale': {
                await Promise.all([
                    this.$store.dispatch('geolocation/updateRequestedLocale', ctx.req.l10n.requestedLocale),
                    this.$store.dispatch('geolocation/updateDesiredLocale', ctx.req.l10n.desiredLocale),
                    this.$store.dispatch('geolocation/updateFinalLocale', ctx.req.l10n.requestedLocale),
                ]);
                break;
            }
            case 'language': {
                await Promise.all([
                    this.$store.dispatch('geolocation/updateRequestedLocale', ctx.req.l10n.requestedLocale),
                    this.$store.dispatch('geolocation/updateDesiredLocale', ctx.req.l10n.desiredLocale),
                    this.$store.dispatch('geolocation/updateFinalLocale', ctx.req.l10n.requestedLocale),
                    this.$store.dispatch('geolocation/updateIsLanguageAwareFlag', true),
                    this.$store.dispatch('geolocation/updateLanguage', ctx.req.l10n.language),
                ]);
                break;
            }
            default: {
                // Fallback for containers which are not using `ServerPrefetchLocalizedProbe`
                await this.$store.dispatch('geolocation/updateFinalLocale', this.$route.params.locale);
                break;
            }
        }

        ctx.res.pocket.splits = await ctx.Synced.slicesData.query('splits', 'null') ?? {};
        ctx.res.pocket.matomo = { baseUrl: ctx.env.HEAD_SITE_MAIN_PUBLIC_BASE_URL_MATOMO };
        ctx.res.pocket.public_context = ctx.public;

        // placeholders to rewrite on LB
        ctx.res.pocket.clientinfo = {
            ipSource: '%%CLIENT_IP_SOURCE%%',
            ja3: '%%CLIENT_JA3%%',
            ja4: '%%CLIENT_JA4%%',
            cfScore: '%%CLIENT_BOT_SCORE%%',
            cfSource: '%%CLIENT_BOT_SOURCE%%',
            cfVerified: '%%CLIENT_BOT_VERIFIED%%',
            cfVerifiedCat: '%%CLIENT_BOT_VERIFIED_CATEGORY%%',
            cfTags: '%%CLIENT_BOT_TAGS%%',
            cfIds: '%%CLIENT_BOT_IDS%%',
        };
    },
});
</script>
